const CAMPAIGN_MARKER = '_mb_gift_campaign';

export function createGiftRuntime({ fetchImpl, root = '/', config, monotonicNow = () => performance.now(), onStatus = () => {}, onCartUpdate = () => {} }) {
  const start = Date.parse(config.startsAt);
  const end = Date.parse(config.endsAt);
  const variantId = String(config.variantId);
  let serverEpoch = Number(config.initialServerMs);
  let serverMono = monotonicNow();
  let queue = Promise.resolve();
  const productTags = new Map();

  function campaignNow() { return serverEpoch + (monotonicNow() - serverMono); }
  function updateServerClock(response) {
    const dateHeader = response.headers && response.headers.get && response.headers.get('Date');
    if (!dateHeader) return;
    const parsed = Date.parse(dateHeader);
    const current = campaignNow();
    // Date headers have whole-second precision. Ignore equal/older samples rather than
    // replacing the extrapolated clock with a rounded-down value near a campaign edge.
    if (Number.isFinite(parsed) && parsed > current) { serverEpoch = parsed; serverMono = monotonicNow(); }
  }
  async function request(path, body) {
    const response = await fetchImpl(root.replace(/\/$/, '/') + path, body ? {
      method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body)
    } : { credentials: 'same-origin' });
    updateServerClock(response);
    if (!response.ok) throw new Error('Não foi possível atualizar a sacola. Tente novamente antes de finalizar.');
    return response.json();
  }
  function marked(item) { return item && item.properties && item.properties[CAMPAIGN_MARKER] === config.campaign; }
  async function withGiftMetadata(cart) {
    const items = await Promise.all((cart.items || []).map(async item => {
      if (item.tags || typeof item.isGift === 'boolean' || typeof item.isGiftCard === 'boolean' || item.gift_card === true ||
          String(item.variant_id) === variantId || String(item.product_type || '').trim().toLowerCase() === 'brinde') return item;
      if (!item.handle) return item;
      if (!productTags.has(item.handle)) {
        const product = await request(`products/${encodeURIComponent(item.handle)}.js`);
        productTags.set(item.handle, Array.isArray(product.tags) ? product.tags : String(product.tags || '').split(',').map(tag => tag.trim()));
      }
      return { ...item, tags: productTags.get(item.handle) };
    }));
    return { ...cart, items };
  }
  function giftType(item) {
    const tags = Array.isArray(item.tags) ? item.tags : [];
    return item.isGift === true || item.isGiftCard === true || marked(item) || String(item.variantId ?? item.variant_id) === variantId || item.gift_card === true ||
      String(item.product_type || '').trim().toLowerCase() === 'brinde' || tags.some(tag => String(tag).trim().toLowerCase() === 'brinde');
  }
  function qualifying(cart) {
    const subtotal = (cart.items || []).reduce((total, item) => {
      if (!item || Number(item.quantity) <= 0 || giftType(item)) return total;
      const cents = Number(item.finalLineCents ?? item.final_line_price);
      return Number.isSafeInteger(cents) && cents > 0 ? total + cents : total;
    }, 0);
    return config.giftReady === true && Number.isSafeInteger(subtotal) && subtotal >= config.minimumCents && campaignNow() >= start && campaignNow() < end;
  }
  async function normalize() {
    let cart = await request('cart.js');
    let changed = false;
    const campaignLines = () => (cart.items || []).map((item, index) => ({ item, line: index + 1 }))
      .filter(entry => String(entry.item.variant_id) === variantId && marked(entry.item));

    // Outside the gift window, inspect only campaign-marked lines. Ordinary checkout
    // does not depend on product-tag reads or gift-catalog availability.
    if (config.giftReady !== true || campaignNow() < start || campaignNow() >= end) {
      for (const entry of campaignLines().reverse()) {
        cart = await request('cart/change.js', { line: entry.line, quantity: 0 });
        changed = true;
      }
      if (changed) cart = await request('cart.js');
      return { cart, changed };
    }

    cart = await withGiftMetadata(cart);
    const active = qualifying(cart);
    const lines = campaignLines();
    if (!active) {
      for (const entry of lines.reverse()) {
        cart = await request('cart/change.js', { line: entry.line, quantity: 0 });
        changed = true;
      }
    } else if (!lines.length) {
      await request('cart/add.js', { id: Number(variantId), quantity: 1, properties: { [CAMPAIGN_MARKER]: config.campaign } });
      changed = true;
    } else {
      const keep = lines[0];
      if (Number(keep.item.quantity) !== 1) {
        cart = await request('cart/change.js', { line: keep.line, quantity: 1 });
        changed = true;
      }
      for (const entry of lines.slice(1).reverse()) {
        cart = await request('cart/change.js', { line: entry.line, quantity: 0 });
        changed = true;
      }
    }

    if (changed) cart = await request('cart.js');
    // A missing BXGY discount must never turn the marked line into a paid item.
    const paidMarked = campaignLines().filter(entry => Number(entry.item.final_line_price) > 0);
    for (const entry of paidMarked.reverse()) {
      cart = await request('cart/change.js', { line: entry.line, quantity: 0 });
      changed = true;
    }
    if (paidMarked.length) cart = await request('cart.js');
    return { cart, changed };
  }
  function sync() {
    const run = () => normalize().then(({ cart, changed }) => {
      if (changed) onCartUpdate(cart);
      onStatus(null);
      return cart;
    }).catch(error => { onStatus(error.message || 'Não foi possível verificar a sacola.'); throw error; });
    const current = queue.then(run, run);
    queue = current.catch(() => undefined);
    return current;
  }
  return { sync, campaignNow, qualifying };
}

export function bootGiftRuntime({ window, document }) {
  const host = document.getElementById('MbBfGiftRuntime');
  if (!host) return;
  const root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root || '/';
  const status = host.querySelector('[data-gift-status]');
  const originalFetch = window.fetch.bind(window);
  let timer;
  const config = {
    startsAt: host.dataset.start,
    endsAt: host.dataset.end,
    initialServerMs: Number(host.dataset.nowMs),
    minimumCents: Number(host.dataset.minimumCents),
    variantId: host.dataset.variantId,
    campaign: host.dataset.campaign,
    giftReady: host.dataset.giftReady === 'true'
  };
  const runtime = createGiftRuntime({
    fetchImpl: originalFetch,
    root, config,
    monotonicNow: () => window.performance.now(),
    onStatus: message => {
      if (status) { status.textContent = message || ''; status.hidden = !message; }
      document.querySelectorAll('[data-bf-gift-checkout-error]').forEach(node => { node.hidden = !message; node.textContent = message || ''; });
    },
    onCartUpdate: cart => {
      document.querySelectorAll('.cart-n').forEach(node => {
        node.textContent = cart.item_count;
        node.style.display = cart.item_count > 0 ? 'grid' : 'none';
      });
      const event = typeof window.CustomEvent === 'function' ? new window.CustomEvent('mb:gift-cart-updated', { detail: { cart } }) : null;
      if (event && document.dispatchEvent) document.dispatchEvent(event);
      if (event && window.dispatchEvent) window.dispatchEvent(event);
      if (/\/cart\/?$/.test(window.location.pathname || '') && window.location.reload) window.location.reload();
    }
  });
  function isCartMutation(input, init) {
    const url = typeof input === 'string' ? input : input && input.url || '';
    const method = String(init && init.method || input && input.method || 'GET').toUpperCase();
    return method !== 'GET' && /\/cart\/(add|change|update|clear)(\.js)?(?:\?|$)/.test(url);
  }
  let pendingCartMutations = 0;
  let cartMutationVersion = 0;
  let mutationWaiters = [];
  function settleCartMutation() {
    pendingCartMutations--;
    cartMutationVersion++;
    if (pendingCartMutations === 0) mutationWaiters.splice(0).forEach(resolve => resolve());
    (window.queueMicrotask || queueMicrotask)(() => runtime.sync().catch(() => {}));
  }
  window.fetch = function (...args) {
    const mutation = isCartMutation(args[0], args[1]);
    if (mutation) { pendingCartMutations++; cartMutationVersion++; }
    return originalFetch(...args).then(response => { if (mutation) settleCartMutation(); return response; }, error => { if (mutation) settleCartMutation(); throw error; });
  };
  async function preflight() {
    while (true) {
      while (pendingCartMutations > 0) await new Promise(resolve => mutationWaiters.push(resolve));
      const version = cartMutationVersion;
      await runtime.sync();
      if (pendingCartMutations === 0 && cartMutationVersion === version) return;
    }
  }
  function checkoutTarget(form, submitter) {
    return Boolean((submitter && submitter.name === 'checkout') || (form && /\/checkout(?:\?|$)/.test(form.action || '')));
  }
  let replayingCheckout = false;
  document.addEventListener('submit', event => {
    const form = event.target;
    if (!checkoutTarget(form, event.submitter) || replayingCheckout) return;
    event.preventDefault(); event.stopImmediatePropagation();
    preflight().then(() => {
      replayingCheckout = true;
      if (form.requestSubmit) form.requestSubmit(event.submitter || undefined);
      else form.submit();
      queueMicrotask(() => { replayingCheckout = false; });
    }).catch(() => {});
  }, true);
  document.addEventListener('click', event => {
    const link = event.target.closest && event.target.closest('a[href]');
    if (!link || !/\/checkout(?:\?|$)/.test(link.getAttribute('href') || '')) return;
    event.preventDefault(); event.stopImmediatePropagation();
    preflight().then(() => window.location.assign(link.href)).catch(() => {});
  }, true);
  function scheduleBoundary() {
    (window.clearTimeout || clearTimeout)(timer);
    const nowMs = runtime.campaignNow();
    const phaseTimes = Array.from(document.querySelectorAll('[data-bf-phase]')).flatMap(phase => [Number(phase.dataset.start), Number(phase.dataset.end)]);
    const next = [Date.parse(config.startsAt), Date.parse(config.endsAt), ...phaseTimes].filter(time => time > nowMs).sort((a, b) => a - b)[0];
    if (next) timer = (window.setTimeout || setTimeout)(() => { refreshCampaignState(); }, Math.max(0, next - nowMs));
  }
  function updateBanner() {
    const now = runtime.campaignNow();
    if (typeof document.querySelector !== 'function') {
      document.querySelectorAll('[data-bf-gift-banner]').forEach(banner => {
        if (now >= Number(banner.dataset.end)) banner.remove();
        else banner.hidden = now < Number(banner.dataset.start);
      });
      return;
    }
    const host = document.querySelector('[data-bf-phases]');
    if (!host) {
      document.querySelectorAll('[data-bf-gift-banner]').forEach(banner => {
        if (now >= Number(banner.dataset.end)) banner.remove();
        else banner.hidden = now < Number(banner.dataset.start);
      });
      return;
    }
    let readiness = {};
    try { readiness = JSON.parse(host.dataset.readiness || '{}'); } catch (error) { readiness = {}; }
    let active = null;
    host.querySelectorAll('[data-bf-phase]').forEach(phase => {
      const eligible = readiness[phase.dataset.bfPhase] === true && now >= Number(phase.dataset.start) && now < Number(phase.dataset.end);
      if (eligible) active = phase;
    });
    let current = null;
    host.querySelectorAll('[data-bf-phase]').forEach(phase => {
      phase.hidden = phase !== active;
      if (phase === active) {
        current = phase.dataset.bfPhase;
        const image = phase.querySelector('img');
        if (image) { image.loading = 'eager'; image.setAttribute('fetchpriority', 'high'); }
      } else {
        const image = phase.querySelector('img');
        if (image) { image.loading = 'lazy'; image.setAttribute('fetchpriority', 'low'); }
      }
    });
    if (host.dataset.activePhase !== (current || '')) {
      host.dataset.activePhase = current || '';
      const event = typeof window.CustomEvent === 'function' ? new window.CustomEvent('mb:bf-phase-change', { detail: { phase: current } }) : null;
      if (event) document.dispatchEvent(event);
    }
  }
  function refreshCampaignState() {
    updateBanner();
    scheduleBoundary();
    runtime.sync().then(() => { updateBanner(); scheduleBoundary(); }).catch(() => { scheduleBoundary(); });
  }
  document.addEventListener('visibilitychange', () => { if (!document.hidden) refreshCampaignState(); });
  window.addEventListener('focus', refreshCampaignState);
  updateBanner();
  scheduleBoundary();
  runtime.sync().then(() => { updateBanner(); scheduleBoundary(); }).catch(() => {});
}

if (typeof window !== 'undefined' && typeof document !== 'undefined') bootGiftRuntime({ window, document });
